import { anyLeague } from "../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../entities/index.js";
import { BattingRankingStats, DataSection, PitchData, PitchingRankingStats, PitchReprocessed, RankingScopeContext, ResultData, ResultDataNew, ResultType, StatType } from "../../enums/index.js";
import EnumValueProvider from "../../providers/EnumValueProvider.js";
import MultiRankingStatCalculator from "./MultiRankingStatCalculator.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import ResultsSlice from "../../valueObjects/ResultsSlice.js";
import resultsSliceForPitchType from "../../classificators/resultsSlice/resultsSliceForPitchType.js";
import satisfiesAll from "../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../classificators/resultsSlice/pitchingScopeContext.js";
import battingScopeContext from "../../classificators/resultsSlice/battingScopeContext.js";
import RankingStatCaseVerifier from "../../validators/RankingStatCaseVerifier.js";
import sessionWithOldTaggingData from "../../classificators/session/sessionWithOldTaggingData.js";
import ClassifiedPitchesFilter from "../../filters/contextual/ClassifiedPitchesFilter.js";
import ContextualFilterBase from "../../filters/contextual/ContextualFilterBase.js";
import defaultWalkClassifier from '../../classificators/walkClassifier.js';

class BaseOnBallsCalculator extends MultiRankingStatCalculator{
    constructor(
        context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache, contextualResultFilter,
        walkClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (typeof(walkClassifier) !== 'undefined' && typeof(walkClassifier) !== 'function') {
            throw new TypeError('walkClassifier must be an function or undefined');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        super(
            context === RankingScopeContext.Pitching.key
                ? [ PitchingRankingStats.WalksAllowed, PitchingRankingStats.WalkRateAllowed ]
                : [ BattingRankingStats.Walks, BattingRankingStats.WalkRate ]
            ,
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithOldTaggingData,
                satisfiesAll(
                    context === RankingScopeContext.Pitching.key 
                        ? pitchingScopeContext
                        : battingScopeContext
                    ,
                    resultsSliceForPitchType
                )
            ),
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.context = context;
        this.contextualFilter = contextualResultFilter || new ClassifiedPitchesFilter(this.resultsProcessor);
        this.walkClassifier = walkClassifier || defaultWalkClassifier;
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const pitches = this.filterResults(slice.getSmallestSlice(), session, league);

        const stats = this.resultsProcessor.calcStats(
            [StatType.Ratio.key],
            pitches,
            // TODO - simplify it...
            [ [ [] ].concat(
                ResultData.getNewDataPath(PitchData.Result.key, DataSection.Tagging.key, ResultType.Pitch.key)
            ) ],
            false
        );

        const output = {};
        let walks = 0;

        if (!stats || !stats.hasOwnProperty(PitchData.Result.key) || !stats[PitchData.Result.key][StatType.Ratio.key]){
            return output;
        }

        for (const result in stats[PitchData.Result.key][StatType.Ratio.key]) {
            if (!Object.hasOwnProperty.call(stats[PitchData.Result.key][StatType.Ratio.key], result)) {
                continue;
            }

            const count = stats[PitchData.Result.key][StatType.Ratio.key][result].count;
            //walks and home runs are only common here
            if (this.walkClassifier(result)) {
                walks += count;
            }
        }

        // now include walks and total bases
        if (walks > 0) {
            const baseOnBalls = this.supportedParams[0];
            output[baseOnBalls.key] = RankingStat.fromParameter(
                baseOnBalls, this.numberFormatter.format(walks), walks
            );
        }

        // now let's calculate percentages... different divider for batting and pitching
        const walksPercentageWeight = this.getWalkPercentageWeight(pitches);
        if (walksPercentageWeight > 0) {
            const walkPercentageParam = this.supportedParams[1];
            output[walkPercentageParam.key] = RankingStat.fromParameter(
                walkPercentageParam, this.numberFormatter.format(walks / walksPercentageWeight), walksPercentageWeight
            );
        }

        return output;
    }

    getWalkPercentageWeight(pitchingResults) {
        const battersFacedOrPa = [];

        //let's go through pitchingResults
        for (let i = 0; i < pitchingResults.length; i++) {
            const result = pitchingResults[i];
            const batterSessionPaId = this.getBatterId(result);
            if (!batterSessionPaId) {
                throw new TypeError(`Unable to determine batter/session/PA id at index ${i}`);
            }

            // TODO - better plate appearance check
            if (battersFacedOrPa.indexOf(batterSessionPaId) < 0){
                battersFacedOrPa.push(batterSessionPaId);
            }
        }

        return battersFacedOrPa.length;
    }

    getBatterId(result) {
        const dataPath = [
            [ResultDataNew.SessionId.key],
            [ResultDataNew.BatterId.key],
            [ResultDataNew.Data.key, DataSection.Scoring.key, PitchReprocessed.PlateAppearanceR.key]
        ];
        const id = [];
        for (const path of dataPath) {
            const value = this.resultsProcessor.getObjectContent(result, path);
            if (typeof(value) === 'undefined') {
                // TODO - log ?
                return false;
            }
            id.push(value);
        }

        return id.join('-');
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default BaseOnBallsCalculator;